import { environment } from 'src/environments/environment';

export const googleSso = environment.auth?.googleSso;

export const googleSsoAllowedDomains = normalizeSsoDomains(googleSso?.allowedDomains);

/**
 * Google sign in can only redirect when the app is served from authDomain itself, because that is
 * where the /__/auth/ handler lives and where the result comes back through storage. Served from
 * anywhere else, localhost included whichever project it is pointed at, Safari and Firefox
 * partition that storage away and the user returns signed out, so those hosts use a popup instead:
 * a popup hands its result back by postMessage, which partitioning does not affect.
 */
export function googleSsoUsesRedirect(hostname: string): boolean {
  const servedFrom = hostname.trim().toLowerCase();
  const authDomain = environment.firebase?.authDomain?.trim().toLowerCase() ?? '';

  return servedFrom.length > 0 && servedFrom === authDomain;
}

export function normalizeSsoDomains(domains?: readonly string[] | null): string[] {
  return (domains ?? [])
    .map(domain => domain.trim().toLowerCase())
    .filter(domain => domain.length > 0);
}

/**
 * Matches on the full "@domain" suffix so lookalike domains such as
 * "user@nottracit.com.au" are not treated as staff addresses.
 */
export function matchesSsoDomain(email: string | null | undefined, domains: readonly string[]): boolean {
  const normalizedDomains = normalizeSsoDomains(domains);

  if (normalizedDomains.length === 0) {
    return false;
  }

  const normalizedEmail = email?.trim().toLowerCase() ?? '';

  if (!normalizedEmail) {
    return false;
  }

  return normalizedDomains.some(domain => normalizedEmail.endsWith(`@${domain}`));
}

export function getGoogleSsoAllowedDomains(): string[] {
  if (googleSso?.enabled !== true) {
    return [];
  }

  return googleSsoAllowedDomains;
}

/**
 * Client-side view of the SSO enforcement that `requireGoogleForTracitStaff`
 * applies on the backend: true when this email must sign in with Google, and
 * therefore must never be offered a password reset. This exists to keep users
 * out of dead ends; it is not a security control. The backend blocking function
 * and `requestPasswordReset` remain the enforcement points.
 */
export function isGoogleSsoEnforcedEmail(email: string | null | undefined): boolean {
  return matchesSsoDomain(email, getGoogleSsoAllowedDomains());
}

export function isGoogleSsoEmail(email?: string | null): boolean {
  return isGoogleSsoEnforcedEmail(email);
}

export function isSystemAdminIdentity(isAdminClaim: unknown, email: unknown): boolean {
  return isAdminClaim === true
    && isGoogleSsoEnforcedEmail(typeof email === 'string' ? email : null);
}
